import type { EventSourceInit } from "eventsource";

import { EventSource as EventSourceLib } from "eventsource";

/**
 * EventSource subclass that prevents reconnection after fatal application
 * errors while allowing reconnection for transient network disconnects.
 *
 * How it works:
 * - When tRPC sends `event: serialized-error` (the server subscription threw),
 *   we set a `_fatalSeen` flag but do NOT close yet.
 * - When the subsequent `error` event fires (connection closed), we check the
 *   flag. If fatal was seen, we close the EventSource to prevent reconnection.
 *   If not (network blip), we let EventSource reconnect normally.
 *
 * Why not close on `serialized-error` directly? Calling close() there prevents
 * tRPC's ReadableStream from terminating (the `error` event with
 * readyState=CLOSED is what triggers `controller.error()` in tRPC's
 * sseStreamConsumer). Without that, the for-await consumer loop hangs.
 *
 * @author Gabe 2026-02-06
 */
export class FatalAwareEventSource extends EventSourceLib {
  private _fatalSeen = false;

  constructor(url: string | URL, eventSourceInitDict?: EventSourceInit) {
    super(url, eventSourceInitDict);

    // NOTE (Gabe, 2026-02-06): Mark that a fatal application error was
    // received. We don't close here because tRPC's sseStreamConsumer needs
    // the error event (with readyState=CLOSED) to terminate the stream.
    this.addEventListener("serialized-error", () => {
      this._fatalSeen = true;
    });

    // NOTE (Gabe, 2026-02-06): On connection error, close only if a fatal
    // application error was previously received. This lets tRPC's error
    // listener see readyState=CLOSED and call controller.error(), which
    // terminates the stream and triggers onError → toast. For network blips
    // (no prior serialized-error), we do nothing so EventSource reconnects.
    this.addEventListener("error", () => {
      if (this._fatalSeen) {
        this.close();
      }
    });
  }

  get fatalSeen(): boolean {
    return this._fatalSeen;
  }
}
